/**
 * Componente GraphLifes - Gráfico de Fluxo de Vidas
 * 
 * Comportamento das colunas:
 * - Sempre mostra todas as datas do range selecionado
 * - Cada dia do período aparece como uma coluna no gráfico
 * - Dias sem eventos mostram valores zero mas ainda aparecem
 */

import { CalendarToday } from '@mui/icons-material'
import { Box, Grid } from '@mui/material'
import { ChartOptions } from 'chart.js'
import Chart from 'react-chartjs-2'
import { dayjs } from 'utils/custom-date'
import { IPerson } from './types'

export interface IGraphLifesProps {
  data: IPerson[]
  startDate?: string // YYYY-MM-DD format - se não especificado, usa último mês
  endDate?: string   // YYYY-MM-DD format - se não especificado, usa último mês
}

export interface IMetrics {
  totalAdmissions: number
  totalDismissals: number
  totalActiveEmployees: number
  dailyData: IDailyData[]
  dateRange: { start: string; end: string }
}

export interface IDailyData {
  date: string
  admissions: number
  dismissals: number
  activeEmployees: number
}

// Função para gerar array de datas consecutivas
function generateDateRange(startDate: string, endDate: string): string[] {
  const dates: string[] = []
  let current = dayjs(startDate)
  const end = dayjs(endDate)
  
  while (current.isSameOrBefore(end)) {
    dates.push(current.format('YYYY-MM-DD'))
    current = current.add(1, 'day')
  }
  
  return dates
}

// Função para determinar se deve usar skip inteligente
function shouldUseSmartSkip(totalDays: number, daysWithEvents: number, isDefaultRange: boolean): boolean {
  // Sempre mostrar todas as datas do range (nunca usar skip inteligente)
  // Isso garante que para qualquer range selecionado, todas as datas sejam exibidas
  return false
}

// Função para calcular métricas a partir dos dados IPerson[]
function calculateMetrics(data: IPerson[], startDate?: string, endDate?: string): IMetrics {
  // Definir range padrão (último mês) se não fornecido
  const today = dayjs()
  const defaultStart = today.subtract(1, 'month').format('YYYY-MM-DD')
  const defaultEnd = today.format('YYYY-MM-DD')
  
  const rangeStart = startDate || defaultStart
  const rangeEnd = endDate || defaultEnd

  if (!data || data.length === 0) {
    return {
      totalAdmissions: 0,
      totalDismissals: 0,
      totalActiveEmployees: 0,
      dailyData: [],
      dateRange: { start: rangeStart, end: rangeEnd }
    }
  }

  // Incluir todos os beneficiários (funcionários e dependentes)
  const beneficiaries = data

  // Filtrar eventos que estão dentro do range especificado
  const eventsInRange = new Map<string, { admissions: number; dismissals: number }>()

  // Processar inclusões no range
  beneficiaries.forEach(person => {
    if (person.hiringDate &&
        dayjs(person.hiringDate).isSameOrAfter(rangeStart) &&
        dayjs(person.hiringDate).isSameOrBefore(rangeEnd)) {
      const date = dayjs(person.hiringDate).format('YYYY-MM-DD')
      const current = eventsInRange.get(date) || { admissions: 0, dismissals: 0 }
      current.admissions += 1
      eventsInRange.set(date, current)
    }
  })

  // Processar exclusões no range
  beneficiaries.forEach(person => {
    if (person.dismissalDate &&
        dayjs(person.dismissalDate).isSameOrAfter(rangeStart) &&
        dayjs(person.dismissalDate).isSameOrBefore(rangeEnd)) {
      const date = dayjs(person.dismissalDate).format('YYYY-MM-DD')
      const current = eventsInRange.get(date) || { admissions: 0, dismissals: 0 }
      current.dismissals += 1
      eventsInRange.set(date, current)
    }
  })

  // Gerar todas as datas no range
  const allDatesInRange = generateDateRange(rangeStart, rangeEnd)
  const daysWithEvents = eventsInRange.size
  
  // Verificar se está usando o range padrão
  const isDefaultRange = !startDate && !endDate
  
  // Decidir se usar skip inteligente ou mostrar todas as datas
  const useSmartSkip = shouldUseSmartSkip(allDatesInRange.length, daysWithEvents, isDefaultRange)
  
  // Calcular vidas ativas no início do período
  let initialActiveEmployees = 0
  beneficiaries.forEach(person => {
    const hired = person.hiringDate && dayjs(person.hiringDate).isBefore(rangeStart)
    const notDismissed = !person.dismissalDate || dayjs(person.dismissalDate).isAfter(rangeStart)
    if (hired && notDismissed) {
      initialActiveEmployees += 1
    }
  })

  const dailyData: IDailyData[] = []
  let cumulativeEmployees = initialActiveEmployees

  if (useSmartSkip) {
    // Usar apenas datas com eventos + primeira e última data do range
    // Isso reduz a quantidade de colunas quando há longos períodos sem eventos
    const relevantDates = new Set([rangeStart, rangeEnd, ...Array.from(eventsInRange.keys())])
    const sortedRelevantDates = Array.from(relevantDates).sort()
    
    sortedRelevantDates.forEach(date => {
      const events = eventsInRange.get(date) || { admissions: 0, dismissals: 0 }
      cumulativeEmployees += events.admissions - events.dismissals
      
      dailyData.push({
        date,
        admissions: events.admissions,
        dismissals: events.dismissals,
        activeEmployees: cumulativeEmployees
      })
    })
  } else {
    // Mostrar todas as datas no range (padrão para último mês e ranges ≤30 dias)
    // Garante mínimo de 30 colunas para o filtro padrão do último mês
    allDatesInRange.forEach(date => {
      const events = eventsInRange.get(date) || { admissions: 0, dismissals: 0 }
      cumulativeEmployees += events.admissions - events.dismissals
      
      dailyData.push({
        date,
        admissions: events.admissions,
        dismissals: events.dismissals,
        activeEmployees: cumulativeEmployees
      })
    })
  }

  // Calcular totais no range especificado
  const totalAdmissions = Array.from(eventsInRange.values()).reduce((sum, events) => sum + events.admissions, 0)
  const totalDismissals = Array.from(eventsInRange.values()).reduce((sum, events) => sum + events.dismissals, 0)
  const finalActiveEmployees = dailyData.length > 0 ? dailyData[dailyData.length - 1].activeEmployees : initialActiveEmployees

  return {
    totalAdmissions,
    totalDismissals,
    totalActiveEmployees: finalActiveEmployees,
    dailyData,
    dateRange: { start: rangeStart, end: rangeEnd }
  }
}

const plugins = [
  {
    id: 'barPattern',
    beforeDraw: (chart: any) => {
      const {
        ctx,
        scales: { x, y },
      } = chart
      const yAxis = y.getPixelForValue(0)

      ctx.save()
      ctx.beginPath()
      ctx.moveTo(x.left, yAxis)
      ctx.lineTo(x.right, yAxis)
      ctx.lineWidth = 1
      ctx.strokeStyle = 'black'
      ctx.stroke()
      ctx.restore()
    },
  },
]

export function GraphLifes({ data, startDate, endDate }: IGraphLifesProps) {
  const metrics = calculateMetrics(data, startDate, endDate)
  const { totalAdmissions, totalDismissals, totalActiveEmployees, dailyData, dateRange } = metrics

  if (dailyData.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <p>Não há dados suficientes para exibir o gráfico no período selecionado</p>
        <p style={{ color: '#666', fontSize: '0.9rem' }}>
          Período: {dayjs(dateRange.start).format('DD/MM/YYYY')} - {dayjs(dateRange.end).format('DD/MM/YYYY')}
        </p>
      </Box>
    )
  }

  // Preparar dados para o gráfico
  const labels = dailyData.map((data) => dayjs(data.date).format('DD MMM'))
  const inclusions = dailyData.map((data) => data.admissions)
  const exclusions = dailyData.map((data) => data.dismissals * -1) // Valores negativos para aparecer abaixo do eixo
  const activeEmployees = dailyData.map((data) => data.activeEmployees)

  const maxInclusions = Math.max(...inclusions, 0) + 10
  const maxExclusions = Math.min(...exclusions, 0) - 2

  // Calcular variação percentual baseada no período
  const initialEmployees = dailyData[0]?.activeEmployees - (dailyData[0]?.admissions || 0) + (dailyData[0]?.dismissals || 0)
  const netChange = totalAdmissions - totalDismissals
  const percentageChange = initialEmployees > 0 ? (netChange / initialEmployees) * 100 : 0
  const percentageText = `${percentageChange >= 0 ? '+' : '-'}${Math.abs(percentageChange).toFixed(2)}%`

  const chartData = {
    labels,
    datasets: [
      {
        type: 'line' as const,
        label: 'Total de Vidas',
        borderColor: '#3024E1',
        pointBackgroundColor: '#3024E1',
        borderWidth: 2,
        tension: 0.2,
        data: activeEmployees,
        yAxisID: 'y1',
      },
      {
        type: 'bar' as const,
        label: 'Inclusões',
        backgroundColor: '#4CAF50',
        data: inclusions,
        borderRadius: 16,
        yAxisID: 'y',
      },
      {
        type: 'bar' as const,
        label: 'Exclusões',
        backgroundColor: '#DE3730',
        data: exclusions,
        borderRadius: 16,
        yAxisID: 'y',
      },
    ],
  }

  const options: ChartOptions<any> = {
    responsive: true,
    scales: {
      x: {
        stacked: false,
      },
      y: {
        stacked: false,
        ticks: {
          padding: 10,
        },
        min: maxExclusions,
        max: maxInclusions,
      },
      y1: {
        display: true,
        position: 'right' as const,
        grid: {
          drawOnChartArea: false,
        },
      },
    },
    barPercentage: 0.4,
    plugins: {
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label
            const value = Math.abs(context.raw)
            return `${label}: ${value}`
          },
          title: () => '',
        },
      },
      datalabels: {
        display: false,
      },
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          usePointStyle: true,
          pointStyle: 'rectRounded',
        },
      },
    },
  }

  return (
    <Box sx={{ 
      backgroundColor: 'white',
      borderRadius: 2,
      p: 3,
      boxShadow: 1
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3 
      }}>
        <Box>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>
            Fluxo de Vidas
          </h2>
          <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
            Controle de entrada e saída de vidas
          </p>
        </Box>

        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          color: '#666',
          fontSize: '0.9rem'
        }}>
          <CalendarToday fontSize="small" />
          <span>{dayjs(dateRange.start).format('DD/MM/YYYY')}</span>
          <span>-</span>
          <CalendarToday fontSize="small" />
          <span>{dayjs(dateRange.end).format('DD/MM/YYYY')}</span>
        </Box>
      </Box>

      {/* Widgets */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Box>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#666' }}>
                Inclusões
              </h3>
              <p style={{ margin: 0 }}>
                <span style={{ color: '#4CAF50', fontSize: '1.5rem', fontWeight: 'bold' }}>
                  {totalAdmissions}
                </span>
              </p>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#666' }}>
                Exclusões
              </h3>
              <p style={{ margin: 0 }}>
                <span style={{ color: '#DE3730', fontSize: '1.5rem', fontWeight: 'bold' }}>
                  {totalDismissals}
                </span>
              </p>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#666' }}>
                Vidas
              </h3>
              <p style={{ margin: 0 }}>
                <span style={{ color: '#3024E1', fontSize: '1.5rem', fontWeight: 'bold' }}>
                  {totalActiveEmployees}
                </span>
                <span style={{ 
                  color: percentageChange >= 0 ? '#4CAF50' : '#DE3730',
                  fontSize: '0.9rem',
                  marginLeft: '8px'
                }}>
                  {percentageText}
                </span>
              </p>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Gráfico */}
      <Chart
        type='bar'
        data={chartData}
        options={options}
        plugins={plugins}
      />
    </Box>
  )
}
